import { RiskStatus } from '@app/src/types/resourceExplorer'
import { addDays, differenceInCalendarDays, parseISO, startOfDay } from 'date-fns'

export interface ChartItem {
  createdAt: string
  riskStatus: RiskStatus
}

interface ChartDataPoint {
  timestamp: number
  riskStatus: RiskStatus
  value: number
}

export const RISK_LEVEL_VALUES: Record<string, number> = {
  [RiskStatus.Low]: 0,
  [RiskStatus.Medium]: 1,
  [RiskStatus.High]: 2,
  [RiskStatus.Extreme]: 3,
}

const getDaysTheGraphSpans = (chartData: ChartDataPoint[]): number => {
  if (chartData.length === 0) return 0
  return differenceInCalendarDays(chartData[chartData.length - 1].timestamp, chartData[0].timestamp)
}

const getIntervalInDays = (chartData: ChartDataPoint[]): number => {
  const daysTheGraphSpans = getDaysTheGraphSpans(chartData)
  if (daysTheGraphSpans <= 5) return 1
  return Math.ceil(daysTheGraphSpans / 5)
}

const transformChartData = (items: ChartItem[]): ChartDataPoint[] => {
  const sortedItems = items.toSorted((a, b) => parseISO(a.createdAt).getTime() - parseISO(b.createdAt).getTime())

  return sortedItems
    .filter(item => item.riskStatus !== RiskStatus.NotApplicable)
    .map(item => ({
      timestamp: parseISO(item.createdAt).getTime(),
      riskStatus: item.riskStatus,
      value: RISK_LEVEL_VALUES[item.riskStatus] ?? 0,
    }))
}

const generateTicks = (start: Date, intervalInDays: number, tickCount: number): number[] =>
  Array.from({ length: tickCount }, (_, i) => addDays(start, i * intervalInDays).getTime())

const getTickCountToExtendPastEnd = (totalDays: number, intervalInDays: number): number => {
  const intervalsToReachEnd = Math.ceil(totalDays / intervalInDays)
  const lastTickLandsOnEnd = totalDays % intervalInDays === 0
  return lastTickLandsOnEnd ? intervalsToReachEnd + 2 : intervalsToReachEnd + 1
}

const calculateGraphTicks = (chartData: ChartDataPoint[]): number[] => {
  if (chartData.length === 0) return []

  const firstDate = startOfDay(chartData[0].timestamp)
  const lastDate = startOfDay(chartData[chartData.length - 1].timestamp)

  if (chartData.length === 1) {
    return generateTicks(firstDate, 1, 3)
  }

  const intervalInDays = getIntervalInDays(chartData)
  const totalDays = differenceInCalendarDays(lastDate, firstDate)
  const tickCount = getTickCountToExtendPastEnd(totalDays, intervalInDays)

  return generateTicks(firstDate, intervalInDays, tickCount)
}

export const prepareChartData = (items: ChartItem[]): { chartData: ChartDataPoint[]; graphTicks: number[] } => {
  const chartData = transformChartData(items)
  const graphTicks = calculateGraphTicks(chartData)
  return { chartData, graphTicks }
}
